import { useEffect, useState } from 'react';
import type { Lot } from '../data/mockLots';

export type LotState = 'live' | 'scheduled' | 'closed';

// Grace window before we fall back to a local "closed" verdict. Source of truth
// is the server (closedAt set, or WS lot_closed); a last-millisecond bid that
// extends the timer must reach us before we flip UI into the closed state,
// otherwise the user sees "ended → re-opened" flicker.
const LOCAL_CLOSE_GRACE_MS = 3000;

export function deriveLotState(lot: Lot, nowMs: number = Date.now()): LotState {
  if (lot.closedAt != null) return 'closed';
  // Liveness is the backend's call, not the wall clock: a lot stays 'scheduled'
  // until the server actually starts it (`startedAt` set on DO init). Inferring
  // 'live' the instant `scheduled_start_at` passes would race the DO and, with
  // no live_state yet, render a fabricated timer.
  if (lot.startedAt == null) return 'scheduled';
  if (lot.timerEndsAt + LOCAL_CLOSE_GRACE_MS <= nowMs) return 'closed';
  return 'live';
}

export function useLotState(lot: Lot | null, tickMs: number = 1000): LotState | null {
  const [state, setState] = useState<LotState | null>(() => (lot ? deriveLotState(lot) : null));

  useEffect(() => {
    if (!lot) {
      setState(null);
      return;
    }
    setState(deriveLotState(lot));
    const id = window.setInterval(() => setState(deriveLotState(lot)), tickMs);
    return () => window.clearInterval(id);
  }, [lot, tickMs]);

  return state;
}
